import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { farmerAPI, productAPI } from '../../api/api';
import { useApi } from '../../api/useApi';
import useIsMobile from '../../hooks/useIsMobile';
import FarmLifecycleLoader from '../common/FarmLifecycleLoader';

const getInitials = (name) =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const buildAvatar = (farmer) => {
  if (farmer.fieldPhoto) return farmer.fieldPhoto;
  const initials = encodeURIComponent(getInitials(farmer.name) || 'FR');
  return `https://ui-avatars.com/api/?name=${initials}&background=2d6a4f&color=ffffff&size=220`;
};

export default function FarmerList() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    data: publicFarmersData,
    loading: loadingPublicFarmers,
    error: publicFarmersError,
  } = useApi(farmerAPI.getPublic, null, { staleTime: 45000 });
  const {
    data: productsData,
    loading: loadingProducts,
    error: productsError,
  } = useApi(productAPI.getAll, null, { staleTime: 45000 });

  const fallbackFarmers = useMemo(() => {
    const products = Array.isArray(productsData) ? productsData : [];
    const farmerMap = {};

    products.forEach((product) => {
      const farmer = product?.farmerId;
      const farmerId = farmer?._id;
      if (!farmerId) return;

      if (!farmerMap[farmerId]) {
        const district = String(farmer?.district || '').trim();
        const state = String(farmer?.state || '').trim();

        farmerMap[farmerId] = {
          _id: farmerId,
          name: farmer?.name || 'Verified Farmer',
          district,
          state,
          locationLabel: district && state ? `${district}, ${state}` : district || state || 'India',
          fieldPhoto: '',
          productCount: 0,
        };
      }

      farmerMap[farmerId].productCount += 1;
    });

    return Object.values(farmerMap);
  }, [productsData]);

  const farmers = useMemo(() => {
    const primary = Array.isArray(publicFarmersData) ? publicFarmersData : [];
    if (primary.length > 0) return primary;
    return fallbackFarmers;
  }, [publicFarmersData, fallbackFarmers]);

  const loading = loadingPublicFarmers && loadingProducts && farmers.length === 0;
  const error = publicFarmersError && productsError ? publicFarmersError : '';

  if (loading) {
    return (
      <section id="farmers" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#fefae0', textAlign: 'center' }}>
        <FarmLifecycleLoader compact title="Loading farmer network" subtitle="Connecting customers to nearby verified farmers..." />
      </section>
    );
  }

  if (error && farmers.length === 0) {
    return (
      <section id="farmers" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#fefae0', textAlign: 'center' }}>
        <p style={{ color: '#dc2626' }}>Unable to load farmers: {error}</p>
      </section>
    );
  }

  return (
    <section id="farmers" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#fefae0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <span
            style={{
              background: '#d8f3dc',
              color: '#2d6a4f',
              padding: '6px 20px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Our Farmers
          </span>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '700', color: '#1a1a2e', marginTop: '16px' }}>
            Meet the Hands Behind Your Food
          </h2>
          <p style={{ color: '#6b7280', marginTop: '12px', fontSize: '1.1rem' }}>
            Verified farmers and their current product counts from the live marketplace.
          </p>
        </div>

        {farmers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px', color: '#6b7280' }}>
            No verified farmers are visible right now.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 240 : 320}px, 1fr))`,
              gap: isMobile ? '16px' : '32px',
            }}
          >
            {farmers.map((farmer) => (
              <div
                key={farmer._id}
                onClick={() => navigate(`/farmer/${farmer._id}`)}
                style={{
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  background: 'white',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.18)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{ height: '180px', overflow: 'hidden', position: 'relative' }}>
                  <img src={buildAvatar(farmer)} alt={farmer.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                    }}
                  />
                </div>

                <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <img
                    src={buildAvatar(farmer)}
                    alt={farmer.name}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      border: '3px solid #2d6a4f',
                      objectFit: 'cover',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1a1a2e' }}>{farmer.name}</h3>
                    <p style={{ color: '#6b7280', fontSize: '13px' }}>{farmer.locationLabel || 'India'}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                      <span style={{ color: '#2d6a4f', fontSize: '13px', fontWeight: '700' }}>{farmer.productCount || 0} products</span>
                      <span style={{ color: '#6b7280', fontSize: '12px' }}>Verified farmer</span>
                    </div>
                  </div>
                  <button
                    style={{
                      background: '#d8f3dc',
                      color: '#2d6a4f',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '999px',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
